import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';
import API_BASE_URL from '../config/api';
import '../styles/Home.css';

const Home = () => {
  const { user, plan } = useAuth();
  const navigate = useNavigate();
  const [report, setReport]               = useState(null);
  const [reportLoading, setReportLoading] = useState(true);
  const [reportError, setReportError]     = useState(null);
  const [expandedDates, setExpandedDates]             = useState(new Set());
  const [expandedFundingTimes, setExpandedFundingTimes] = useState(new Set());
  const [checkoutLoading, setCheckoutLoading] = useState(null); // 'monthly' | 'annual' | null

  const handleSubscribe = async (targetPlan) => {
    if (!user) { navigate('/login'); return; }
    setCheckoutLoading(targetPlan);
    try {
      const res = await fetch(`${API_BASE_URL}/api/subscriptions/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.sub, plan: targetPlan }),
      });
      const data = await res.json();
      if (data.success && data.url) window.location.href = data.url;
    } catch (err) {
      console.error('Checkout failed:', err);
    } finally {
      setCheckoutLoading(null);
    }
  };

  const LockIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      style={{ verticalAlign: 'middle' }}>
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
    </svg>
  );

  const EyeIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      style={{ verticalAlign: 'middle' }}>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
      <circle cx="12" cy="12" r="3"></circle>
    </svg>
  );

  const REPORT_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const fmtReportDate = (iso) => {
    const d = new Date(iso + 'T12:00:00');
    return `${String(d.getDate()).padStart(2,'0')}.${REPORT_MONTHS[d.getMonth()]}.${d.getFullYear()}`;
  };
  const fmtPct = (val) => val != null ? `${(val * 100).toFixed(2)}%` : '—';

  const toggleDate = (dateStr) => {
    setExpandedDates(prev => {
      const next = new Set(prev);
      next.has(dateStr) ? next.delete(dateStr) : next.add(dateStr);
      return next;
    });
  };

  const toggleFundingTime = (key) => {
    setExpandedFundingTimes(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const fmtFundingTime = (ftKey) => {
    if (!ftKey || ftKey === '__no_ft__') return 'unknown time';
    const d = new Date(ftKey);
    return `${String(d.getUTCHours()).padStart(2,'0')}:${String(d.getUTCMinutes()).padStart(2,'0')} UTC`;
  };

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const res  = await fetch(`${API_BASE_URL}/api/opportunity-report`);
        const data = await res.json();
        if (data.success) setReport(data);
        else setReportError(data.error || 'Failed to load report');
      } catch (err) {
        setReportError('Failed to load report');
      } finally {
        setReportLoading(false);
      }
    };
    fetchReport();
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const scrollToNavigation = () => {
    const element = document.getElementById('navigation');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const navigationItems = [
    { id: 'what-is', title: 'What is Micropincher', icon: '?' },
    { id: 'who-for', title: 'Who is it for', icon: '👤' },
    { id: 'what-expect', title: 'What to expect', icon: '◉' },
    { id: 'what-not', title: 'What it is not', icon: '⊗' },
    { id: 'who-we-are', title: 'Who we are', icon: '▣' },
    { id: 'how-works', title: 'How it works', icon: '⚙' },
    { id: 'roadmap', title: 'Roadmap', icon: '◬' },
    { id: 'how-use', title: 'How to use', icon: '▤' },
    { id: 'security', title: 'Security', icon: '⊠' },
    { id: 'pricing', title: 'Pricing', icon: '$' },
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Micropincher",
    "description": "Crypto analysis and trading simulation of arbitrage strategies, prevent trading loss. Learn arbitrage strategy (funding fee), before you apply it in reality.",
    "email": "info@micropincher.com"
  };

  // Pricing card styles
  const planCard = {
    flex: '1 1 200px', maxWidth: '260px',
    background: '#1a1b23', border: '1px solid #2a2d3a',
    borderRadius: '10px', padding: '20px',
  };
  const planName = { fontSize: '13px', color: '#8b949e', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' };
  const planPrice = { fontSize: '28px', fontWeight: 700, color: '#ffffff', marginBottom: '16px' };
  const planPer = { fontSize: '13px', fontWeight: 400, color: '#8b949e' };
  const planFeatures = { listStyle: 'none', padding: 0, margin: '0 0 20px', display: 'flex', flexDirection: 'column', gap: '6px' };
  const planFeatureItem = (included) => ({ fontSize: '12px', color: included ? '#e6edf3' : '#484f58' });
  const planBtnPrimary = {
    width: '100%', padding: '8px', background: '#2a2d3a',
    border: '1px solid #3a3d4a', borderRadius: '6px',
    color: '#ffffff', fontSize: '12px', cursor: 'pointer', fontWeight: 500,
  };
  const planBtnHighlight = { ...planBtnPrimary, background: '#58a6ff', border: 'none', color: '#0a0b0f' };
  const planBtnSecondary = {
    display: 'block', width: '100%', padding: '8px', textAlign: 'center',
    background: 'none', border: '1px solid #3a3d4a', borderRadius: '6px',
    color: '#8b949e', fontSize: '12px', textDecoration: 'none', fontWeight: 500,
    boxSizing: 'border-box',
  };
  const planCurrent = {
    textAlign: 'center', fontSize: '12px', color: '#3fb950',
    padding: '8px', border: '1px solid #3fb950', borderRadius: '6px',
  };
  const bestValueBadge = {
    position: 'absolute', top: '-10px', left: '50%', transform: 'translateX(-50%)',
    background: '#58a6ff', color: '#0a0b0f', fontSize: '10px', fontWeight: 700,
    padding: '2px 10px', borderRadius: '10px', whiteSpace: 'nowrap',
  };

  return (
    <div className="home-container">
      <SEO
        title="Micropincher - Crypto Trading Arbitrage Strategy Analysis and trading simulation"
        description="Crypto analysis and trading simulation of arbitrage strategies, prevent trading loss. Learn arbitrage strategy (funding fee), before you apply it in reality."
        keywords="crypto arbitrage, trading simulator, funding fees, risk management, crypto training tool"
        ogTitle="Micropincher - Crypto Arbitrage Trading Analysis and Simulation Tool"
        ogDescription="Test crypto trading arbitrage strategy. Analysis of market and simulation will help you to learn the trading and prevent loss. Trade only with managed risk, Don't trade like a gambler."
        ogImage="https://micropincher.com/images/og-image.jpg"
        ogUrl="https://micropincher.com/"
        twitterImage="https://micropincher.com/images/og-image.jpg"
        canonicalUrl="https://micropincher.com/"
        structuredData={structuredData}
      />
      <div className="home-content">
        <div className="home-header">
          <h1 className="home-title">Welcome to Micropincher</h1>
          <div className="home-subtitle-block">
            <p className="home-subtitle">
              A crypto analysis platform designed for:
            </p>
            <ul className="home-subtitle-list">
              <li>funding rate strategy indicators</li>
              <li>orderbook analysis in real time</li>
              <li>hedging positions calculation</li>
              <li>trading simulation</li>
              <li>arbitrage strategies trading automation</li>
              <li>other arbitrage strategies are in development or testing ...</li>
            </ul>
          </div>
        </div>

        {/* Latest Calculated Opportunities */}
        <div className="home-trades-section">
          <div className="home-trades-inner">
            <h2 className="home-trades-title">Latest Calculated Opportunities</h2>
            <p className="home-trades-subtitle">
              Summary of calculated opportunities within the last 5 days. Final profit is calculated
              with already deducted fees (taker fees are included in the trade simulation). The ideal
              report takes all the opportunities; the conservative report takes into account that you
              utilize all available assets on the exchange, so you cannot have two opportunities
              running on one exchange in parallel.{' '}
              <button
                onClick={() => scrollToSection('pricing')}
                style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', cursor: 'pointer' }}
                className="cta-link"
              >
                Register and select a Monthly or Annual plan to see the actual opportunities you can trade.
              </button>
            </p>

            <div className="trade-list-container" style={{ overflowX: 'auto' }}>
              {reportLoading ? (
                <div className="trade-list-empty">Loading...</div>
              ) : reportError ? (
                <div className="trade-list-empty" style={{ color: '#f85149' }}>{reportError}</div>
              ) : (
                <table className="trade-list-table" style={{ minWidth: '600px', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ lineHeight: 1.3 }}>
                      <th style={{ width: '28px' }}></th>
                      <th style={{ verticalAlign: 'top' }}>DATE</th>
                      <th>IDEAL<br/>COUNT</th>
                      <th>IDEAL PROFIT<br/>SUM (avg)</th>
                      <th>CONSERV.<br/>COUNT</th>
                      <th>CONSERV. PROFIT<br/>SUM (avg)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Today row — locked / unlocked depending on plan */}
                    {(() => {
                      const todayIso  = new Date().toISOString().slice(0, 10);
                      const hasPlan   = ['monthly', 'annual'].includes(plan);
                      const iconColor = hasPlan ? '#3fb950' : '#8b949e';
                      const cellStyle = { color: iconColor, fontSize: '14px' };

                      const tooltip = hasPlan
                        ? 'View today\'s opportunities'
                        : user
                          ? 'Select a Monthly or Annual plan to see today\'s opportunities'
                          : 'To see upcoming opportunities, register and select the right plan';

                      const handleTodayClick = () => {
                        if (hasPlan)       navigate('/opportunities');
                        else if (user)     scrollToSection('pricing');
                        else               navigate('/login');
                      };

                      return (
                        <tr
                          title={tooltip}
                          onClick={handleTodayClick}
                          style={{ background: '#161b22', cursor: 'pointer' }}
                        >
                          <td style={{ textAlign: 'center', color: iconColor }}>
                            {hasPlan ? <EyeIcon /> : <LockIcon />}
                          </td>
                          <td style={{ ...cellStyle, fontWeight: 500 }}>
                            {fmtReportDate(todayIso)}{' '}
                            <span style={{ color: '#8b949e' }}>
                              {hasPlan ? '(opportunities)' : '(today - click to unlock)'}
                            </span>
                          </td>
                          <td style={cellStyle}>{hasPlan ? <EyeIcon /> : <LockIcon />}</td>
                          <td style={cellStyle}>{hasPlan ? <EyeIcon /> : <LockIcon />}</td>
                          <td style={cellStyle}>{hasPlan ? <EyeIcon /> : <LockIcon />}</td>
                          <td style={cellStyle}>{hasPlan ? <EyeIcon /> : <LockIcon />}</td>
                        </tr>
                      );
                    })()}

                    {/* Historical date rows */}
                    {report && (() => {
                      // Build: byDate[date][ftKey] = [rows...]
                      const byDate = {};
                      for (const row of report.data) {
                        const ftKey = row.funding_time || '__no_ft__';
                        if (!byDate[row.date]) byDate[row.date] = {};
                        if (!byDate[row.date][ftKey]) byDate[row.date][ftKey] = [];
                        byDate[row.date][ftKey].push(row);
                      }
                      const sortedDates = Object.keys(byDate).sort((a, b) => b.localeCompare(a));

                      return sortedDates.map(dateStr => {
                        const ftMap      = byDate[dateStr];
                        const sortedFts  = Object.keys(ftMap).sort();
                        const dateExpanded = expandedDates.has(dateStr);
                        const allRows    = sortedFts.flatMap(k => ftMap[k]);
                        const dateIdealCount = allRows.reduce((s, r) => s + r.ideal_count, 0);
                        const dateConsCount  = allRows.reduce((s, r) => s + r.cons_count, 0);
                        const dateIdealSum = sortedFts.reduce((s, ftKey) => {
                          const rows = ftMap[ftKey];
                          return s + rows.reduce((a, r) => a + r.ideal_profit_avg, 0) / (rows.length || 1);
                        }, 0);
                        const dateConsSum = sortedFts.reduce((s, ftKey) => {
                          const rows = ftMap[ftKey];
                          return s + rows.reduce((a, r) => a + r.cons_profit_avg, 0) / (rows.length || 1);
                        }, 0);

                        return (
                          <React.Fragment key={dateStr}>
                            {/* Date row */}
                            <tr onClick={() => toggleDate(dateStr)} style={{ cursor: 'pointer', background: dateExpanded ? '#1a2030' : undefined }}>
                              <td style={{ textAlign: 'center', color: '#8b949e', fontSize: '11px', userSelect: 'none' }}>
                                {dateExpanded ? '▼' : '▶'}
                              </td>
                              <td style={{ fontWeight: 500 }}>{fmtReportDate(dateStr)}</td>
                              <td>{dateIdealCount}</td>
                              <td style={{ color: '#3fb950' }}>{fmtPct(dateIdealSum)}</td>
                              <td>{dateConsCount}</td>
                              <td style={{ color: '#58a6ff' }}>{fmtPct(dateConsSum)}</td>
                            </tr>

                            {/* Funding time rows */}
                            {dateExpanded && sortedFts.map(ftKey => {
                              const ftRows     = ftMap[ftKey];
                              const ftRowKey   = `${dateStr}__${ftKey}`;
                              const ftExpanded = expandedFundingTimes.has(ftRowKey);
                              const ftIdealCount = ftRows.reduce((s, r) => s + r.ideal_count, 0);
                              const ftIdealSum   = ftRows.reduce((s, r) => s + r.ideal_profit_avg, 0) / (ftRows.length || 1);
                              const ftConsCount  = ftRows.reduce((s, r) => s + r.cons_count, 0);
                              const ftConsSum    = ftRows.reduce((s, r) => s + r.cons_profit_avg, 0) / (ftRows.length || 1);

                              return (
                                <React.Fragment key={ftKey}>
                                  {/* Funding time row */}
                                  <tr onClick={() => toggleFundingTime(ftRowKey)} style={{ cursor: 'pointer', background: '#161b22' }}>
                                    <td></td>
                                    <td style={{ paddingLeft: '16px', fontSize: '12px', userSelect: 'none' }}>
                                      {ftExpanded ? '▼' : '▶'} {fmtFundingTime(ftKey)}
                                    </td>
                                    <td style={{ fontSize: '12px' }}>{ftIdealCount}</td>
                                    <td style={{ color: '#3fb950', fontSize: '12px' }}>{fmtPct(ftIdealSum)}</td>
                                    <td style={{ fontSize: '12px' }}>{ftConsCount}</td>
                                    <td style={{ color: '#58a6ff', fontSize: '12px' }}>{fmtPct(ftConsSum)}</td>
                                  </tr>

                                  {/* Symbol rows */}
                                  {ftExpanded && ftRows.slice().sort((a, b) => a.symbol.localeCompare(b.symbol)).map(r => (
                                    <tr key={r.symbol} style={{ background: '#0d1117' }}>
                                      <td></td>
                                      <td style={{ paddingLeft: '32px', color: '#8b949e', fontSize: '11px' }}>{r.symbol}</td>
                                      <td style={{ color: '#8b949e', fontSize: '11px' }}>{r.ideal_count}</td>
                                      <td style={{ color: '#3fb950', fontSize: '11px' }}>{fmtPct(r.ideal_profit_avg)}</td>
                                      <td style={{ color: '#8b949e', fontSize: '11px' }}>{r.cons_count}</td>
                                      <td style={{ color: '#58a6ff', fontSize: '11px' }}>{fmtPct(r.cons_profit_avg)}</td>
                                    </tr>
                                  ))}
                                </React.Fragment>
                              );
                            })}
                          </React.Fragment>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        <div className="home-cta">
          <p className="cta-text">
            {!user ? (
              <>
                Please <a href="/login" className="cta-link">log in</a> to access the platform
              </>
            ) : (
              <>
                Welcome back! Go to <a href="/opportunities" className="cta-link">Opportunities</a> page to get started
              </>
            )}
          </p>
        </div>

        <div className="home-cta home-cta-education">
          <p className="cta-text">
            New to crypto arbitrage? Visit our{' '}
            <Link to="/education" className="cta-link">Education library</Link>
            {' '}to learn about funding rate strategy, arbitrage techniques and how to calculate profit.
          </p>
        </div>

        <nav id="navigation" className="home-navigation">
          {navigationItems.map((item) => (
            <button
              key={item.id}
              className="nav-tile"
              onClick={() => scrollToSection(item.id)}
            >
              <div className="nav-icon">{item.icon}</div>
              <div className="nav-title">{item.title}</div>
            </button>
          ))}
        </nav>

        <div className="home-sections">
          <section id="what-is" className="info-section">
            <h2>
              What is Micropincher for
              <button className="back-to-nav" onClick={scrollToNavigation} title="Back to navigation">↑</button>
            </h2>
            <p>
              Micropincher is a decision-support tool and trading simulator, for crypto traders who want to approach trading systematically and with controlled risk. You can try how good or bad is certain strategy by simulating the trade and collecting virtual profit, before you lose your money.
            </p>
            <p>
              The project was originally built as a private internal tool for active trading, but due to growing interest, it is now being opened to the public with limited access supported by user contributions.
            </p>
            <p>
              <strong>Micropincher does not promise guaranteed profits.</strong> Its purpose is to surface opportunities, risks, and inefficiencies in crypto markets so users can make informed decisions.
            </p>
          </section>

          <section id="who-for" className="info-section">
            <h2>
              Who Micropincher is for
              <button className="back-to-nav" onClick={scrollToNavigation} title="Back to navigation">↑</button>
            </h2>
            <p>Micropincher is designed for:</p>
            <ul className="features-list">
              <li>Traders interested in arbitrage and market inefficiencies</li>
              <li>Users who want to understand risk before entering a position</li>
              <li>People who prefer data-driven trading over speculation</li>
              <li>Crypto users who want transparency instead of hype</li>
            </ul>
            <p className="note">It is not intended for beginners looking for "get rich quick" solutions.</p>
          </section>

          <section id="what-expect" className="info-section">
            <h2>
              What to expect from the service
              <button className="back-to-nav" onClick={scrollToNavigation} title="Back to navigation">↑</button>
            </h2>
            <p>With Micropincher you can expect:</p>
            <ul className="features-list">
              <li>Visualization of arbitrage opportunities</li>
              <li>Focus on risk-aware strategies, not price prediction</li>
              <li>Clear metrics instead of signals based on emotions</li>
              <li>Tools that support human decision-making, not blind automation</li>
            </ul>
            <p>Currently, Micropincher provides one proven arbitrage strategy based on funding fees, with more planned as the project grows.</p>
          </section>

          <section id="what-not" className="info-section highlight">
            <h2>
              What Micropincher is not (at least for now)
              <button className="back-to-nav" onClick={scrollToNavigation} title="Back to navigation">↑</button>
            </h2>
            <p>Micropincher is not:</p>
            <ul className="features-list">
              <li>An automated trading bot</li>
              <li>A signal-selling service</li>
              <li>A profit guarantee</li>
              <li>A casino-style trading platform</li>
              <li>A free service funded by ads</li>
            </ul>
            <p>We intentionally avoid advertising-driven models to keep the platform focused and independent.</p>
          </section>

          <section id="who-we-are" className="info-section">
            <h2>
              Who we are
              <button className="back-to-nav" onClick={scrollToNavigation} title="Back to navigation">↑</button>
            </h2>
            <p>
              We are developers and traders who built Micropincher for our own use first. Only after validating the approach in real conditions did we decide to make it available to others.
            </p>
            <p>
              The project exists thanks to community financial support, not ads or third-party sponsorships.
            </p>
          </section>

          <section id="how-works" className="info-section">
            <h2>
              How Micropincher works
              <button className="back-to-nav" onClick={scrollToNavigation} title="Back to navigation">↑</button>
            </h2>
            <p>Micropincher aggregates and analyzes data from multiple crypto exchanges to:</p>
            <ul className="features-list">
              <li>Identify arbitrage inefficiencies</li>
              <li>Estimate risk exposure</li>
              <li>Compare funding rates and market conditions</li>
            </ul>
            <p><strong>The platform does not execute trades on your behalf.</strong> You stay in control of every decision.</p>
          </section>

          <section id="roadmap" className="info-section">
            <h2>
              What is on our roadmap
              <button className="back-to-nav" onClick={scrollToNavigation} title="Back to navigation">↑</button>
            </h2>
            <p>Planned improvements depend directly on user interest and support:</p>
            <ul className="features-list">
              <li>Additional arbitrage strategies</li>
              <li>Deeper risk analytics</li>
              <li>Improved visualizations</li>
              <li>Advanced filters and alerts</li>
              <li>Educational content and strategy breakdowns</li>
            </ul>
            <p>More support means faster development and more strategies.</p>
          </section>

          <section id="how-use" className="info-section">
            <h2>
              How to use Micropincher
              <button className="back-to-nav" onClick={scrollToNavigation} title="Back to navigation">↑</button>
            </h2>
            <ul className="features-list numbered">
              <li>Register an account</li>
              <li>Connect supported exchanges (read-only access)</li>
              <li>Explore available strategies and metrics</li>
              <li>Evaluate risks and opportunities</li>
              <li>Make your own trading decisions</li>
            </ul>
            <p><strong>Micropincher is meant to assist, not replace, the trader.</strong></p>
          </section>

          <section id="security" className="info-section security">
            <h2>
              Guarantees and security
              <button className="back-to-nav" onClick={scrollToNavigation} title="Back to navigation">↑</button>
            </h2>
            <p>Security is a core principle of Micropincher:</p>
            <ul className="features-list">
              <li>API keys are read-only</li>
              <li>No trading or withdrawal permissions are required</li>
              <li>Micropincher cannot move funds from your account</li>
              <li>The platform only visualizes balances and market data</li>
            </ul>
            <p className="note">
              By registering, you consent to receive project-related emails and newsletters. We do not sell or share email addresses with third parties.
            </p>
          </section>

          <section id="pricing" className="info-section">
            <h2>
              Pricing
              <button className="back-to-nav" onClick={scrollToNavigation} title="Back to navigation">↑</button>
            </h2>
            <p>Micropincher is free to start. Unlock full access with a paid plan.</p>

            <div style={{ display: 'flex', gap: '16px', marginTop: '24px', flexWrap: 'wrap' }}>

              {/* Free Plan */}
              <div style={planCard}>
                <div style={planName}>Free</div>
                <div style={planPrice}>$0</div>
                <ul style={planFeatures}>
                  <li style={planFeatureItem(true)}>✓ Login &amp; account</li>
                  <li style={planFeatureItem(true)}>✓ Taker / Maker fees</li>
                  <li style={planFeatureItem(true)}>✓ Funding rates</li>
                  <li style={planFeatureItem(false)}>✗ Opportunities</li>
                  <li style={planFeatureItem(false)}>✗ Trades</li>
                </ul>
                {!user ? (
                  <a href="/login" style={planBtnSecondary}>Sign up free</a>
                ) : plan === 'free' ? (
                  <div style={planCurrent}>Current plan</div>
                ) : null}
              </div>

              {/* Monthly Plan */}
              <div style={planCard}>
                <div style={planName}>Monthly</div>
                <div style={planPrice}>$15 <span style={planPer}>/month</span></div>
                <ul style={planFeatures}>
                  <li style={planFeatureItem(true)}>✓ Everything in Free</li>
                  <li style={planFeatureItem(true)}>✓ Opportunities</li>
                  <li style={planFeatureItem(true)}>✓ Trading simulation</li>
                  <li style={planFeatureItem(true)}>✓ Orderbook performance</li>
                  <li style={planFeatureItem(true)}>✓ Hedge calculator</li>
                  <li style={planFeatureItem(true)}>✓ Future paid features</li>
                </ul>
                {plan === 'monthly' ? (
                  <div style={planCurrent}>Current plan</div>
                ) : (
                  <button
                    style={{ ...planBtnPrimary, opacity: checkoutLoading === 'monthly' ? 0.6 : 1 }}
                    disabled={!!checkoutLoading}
                    onClick={() => handleSubscribe('monthly')}
                  >
                    {checkoutLoading === 'monthly' ? 'Redirecting…' : user ? 'Subscribe' : 'Get started'}
                  </button>
                )}
              </div>

              {/* Annual Plan */}
              <div style={{ ...planCard, border: '1px solid #58a6ff', position: 'relative' }}>
                <div style={bestValueBadge}>Best value</div>
                <div style={planName}>Annual</div>
                <div style={planPrice}>$150 <span style={planPer}>/year</span></div>
                <div style={{ fontSize: '11px', color: '#3fb950', marginBottom: '12px' }}>Save $30 vs monthly</div>
                <ul style={planFeatures}>
                  <li style={planFeatureItem(true)}>✓ Everything in Free</li>
                  <li style={planFeatureItem(true)}>✓ Opportunities</li>
                  <li style={planFeatureItem(true)}>✓ Trading simulation</li>
                  <li style={planFeatureItem(true)}>✓ Orderbook performance</li>
                  <li style={planFeatureItem(true)}>✓ Hedge calculator</li>
                  <li style={planFeatureItem(true)}>✓ Future paid features</li>
                </ul>
                {plan === 'annual' ? (
                  <div style={planCurrent}>Current plan</div>
                ) : (
                  <button
                    style={{ ...planBtnHighlight, opacity: checkoutLoading === 'annual' ? 0.6 : 1 }}
                    disabled={!!checkoutLoading}
                    onClick={() => handleSubscribe('annual')}
                  >
                    {checkoutLoading === 'annual' ? 'Redirecting…' : user ? 'Subscribe' : 'Get started'}
                  </button>
                )}
              </div>

            </div>

            <p style={{ marginTop: '16px', fontSize: '11px', color: '#484f58', lineHeight: '1.6' }}>
              By subscribing you agree to our{' '}
              <a href="/terms" style={{ color: '#8b949e' }}>Terms &amp; Conditions</a>
              {' '}and{' '}
              <a href="/privacy" style={{ color: '#8b949e' }}>Privacy Policy</a>.
              Subscriptions renew automatically. Cancel anytime.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Home;
